import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { Button, ProgressBar } from './ui'
import { CookieConsent, CookieSettingsLink } from './CookieConsent'
import { LOCALE_KEY } from '../i18n'
import wordmark from '../assets/bonded-wordmark.webp'

export function Layout({
  children,
  progress,
  showBack = false,
  onBack,
}: {
  children: React.ReactNode
  progress?: number
  showBack?: boolean
  /**
   * Flow-aware back. Deliberately not `navigate(-1)`: browser history includes
   * screens the current branch may no longer contain, so going back through it
   * can land on a question this run never asked.
   */
  onBack?: () => void
}) {
  const { t, i18n } = useTranslation()
  const location = useLocation()

  const switchLanguage = async () => {
    const next = i18n.language === 'el' ? 'en' : 'el'
    localStorage.setItem(LOCALE_KEY, next)
    await i18n.changeLanguage(next)
  }

  return (
    <div className="min-h-dvh flex flex-col">
      <div className="web-cloud-layer" aria-hidden>
        <span className="web-cloud web-cloud-one" />
        <span className="web-cloud web-cloud-two" />
        <span className="web-cloud web-cloud-three" />
      </div>
      <header className="funnel-header flex items-center justify-between gap-3">
        <div className="header-side">
          {showBack && onBack ? (
            <button className="text-button" onClick={onBack} aria-label={t('back')}>
              ← {t('back')}
            </button>
          ) : null}
        </div>
        <img className="wordmark-logo" src={wordmark} alt="Bonded Studio" />
        <div className="header-side flex justify-end">
          <button className="language-button" onClick={switchLanguage} aria-label="Change language">
            {t('language')}
          </button>
        </div>
      </header>
      {progress !== undefined ? (
        <div className="progress-shell">
          <ProgressBar value={progress * 100} />
        </div>
      ) : null}
      <main
        key={location.pathname}
        className="page-enter flex flex-1 flex-col items-center justify-center px-5 py-8 sm:px-8"
      >
        {children}
      </main>
      <footer className="funnel-footer">
        <CookieSettingsLink className="funnel-footer-link" />
        <span>{t('footer.copyright', { year: new Date().getFullYear() })}</span>
      </footer>

      <CookieConsent />
    </div>
  )
}

export function ErrorPanel({ retry }: { retry: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="content-column text-center">
      <div className="sticker-icon" aria-hidden>
        ↻
      </div>
      <p className="body-copy">{t('errors.generic')}</p>
      <Button variant="sunshine" onClick={retry}>
        {t('retry')}
      </Button>
    </div>
  )
}

export function PageSkeleton() {
  return (
    <div className="content-column" aria-label="Loading">
      <div className="skeleton skeleton-label" />
      <div className="skeleton skeleton-title" />
      <div className="skeleton skeleton-card" />
      <div className="skeleton skeleton-button" />
    </div>
  )
}
